import { WithContext, JobPosting } from "schema-dts"
import { Job } from "../contents/job/Job"

/**
 * 求人情報からGoogle Jobs用のJSON-LD構造化データを生成する
 * @param job 求人情報
 * @param jobTitle 翻訳済みの求人タイトル
 * @param jobDescription 翻訳済みの求人説明
 * @param language 言語コード (ja または en)
 * @returns JobPosting JSON-LD
 */
export function generateJobPostingJsonLd(
  job: Job,
  jobTitle: string,
  jobDescription: string,
  language: string = "ja"
): WithContext<JobPosting> {
  const baseUrl = "https://www.mksc.jp"
  const jobUrl = `${baseUrl}/jobs/${job.slug}`

  // slugから投稿日を推測 (例: 20250401-... → 2025-04-01)
  const dateMatch = job.slug.match(/^(\d{4})(\d{2})(\d{2})/)
  const datePosted = dateMatch
    ? `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`
    : new Date().toISOString().split('T')[0]

  // 有効期限を現在から1ヶ月後に設定
  const validThroughDate = new Date()
  validThroughDate.setMonth(validThroughDate.getMonth() + 1)
  const validThrough = validThroughDate.toISOString().split('T')[0]

  // 雇用形態のマッピング
  const employmentTypeMap: Record<string, string> = {
    FULL_TIME: "FULL_TIME",
    PART_TIME: "PART_TIME"
  }

  // 勤務地のマッピング
  const jobLocationMap: Record<string, string> = {
    TELECOMMUTE: "リモートワーク",
    COMMUTE: "愛知県名古屋市中区丸の内1-17-31 清原名古屋ビル5階",
    EITHER: "愛知県名古屋市中区丸の内1-17-31 清原名古屋ビル5階（リモートワーク可）"
  }

  // 給与の単位時間マッピング
  const salaryUnitMap: Record<string, string> = {
    HOUR: "HOUR",
    DAY: "DAY",
    WEEK: "WEEK",
    MONTH: "MONTH",
    YEAR: "YEAR"
  }

  const jsonLd: WithContext<JobPosting> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: jobTitle,
    description: jobDescription,
    identifier: {
      "@type": "PropertyValue",
      name: "株式会社エムケイシステム",
      value: job.slug
    },
    datePosted: datePosted,
    validThrough: validThrough,
    hiringOrganization: {
      "@type": "Organization",
      name: "株式会社エムケイシステム",
      sameAs: baseUrl,
      logo: `${baseUrl}/logo.png`
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        streetAddress: jobLocationMap[job.location] || jobLocationMap.COMMUTE,
        addressLocality: "名古屋市",
        addressRegion: "愛知県",
        postalCode: "460-0002",
        addressCountry: "JP"
      }
    },
    baseSalary: {
      "@type": "MonetaryAmount",
      currency: job.salary.currencyCode,
      value: {
        "@type": "QuantitativeValue",
        minValue: job.salary.min,
        maxValue: job.salary.max,
        unitText: salaryUnitMap[job.salary.unit]
      }
    },
    employmentType: employmentTypeMap[job.employmentType]
  }

  // オプション: 応募者位置要件
  if (job.applicantLocationRequirements) {
    jsonLd.applicantLocationRequirements = job.applicantLocationRequirements
  }

  // オプション: リモートワーク対応
  if (job.location === "TELECOMMUTE" || job.location === "EITHER") {
    jsonLd.jobLocationType = "TELECOMMUTE"
  }

  return jsonLd
}
